<script setup lang="ts">
import { computed, useId } from 'vue'
import {
  SelectContent,
  SelectItem,
  SelectItemIndicator,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'reka-ui'

import { useUnsavedChangesStore } from '@/shared/composables/unsaved-changes'
import { LoadingSpinner } from '@/shared/ui'

import { LOADING_ENGINES } from './copy'
import { useEngineSelectionStore } from './selection-store'
import { useSelectedEngine } from './use-selected-engine'

const { engines, selectedEngine, hasEngines, isPending, isError } = useSelectedEngine()
const selection = useEngineSelectionStore()

const labelId = useId()

// Engines come last created first from the API; the list keeps that order and has no
// blank option. With no engines the dropdown is shown disabled, so it never opens empty.
const value = computed(() => selectedEngine.value?.id)
const placeholder = computed(() =>
  hasEngines.value ? 'Select an engine' : 'Add new dynamic engine',
)

// A page with a change that is not saved yet asks before the engine changes; staying keeps
// the selection, so the dropdown shows the old engine again.
const unsaved = useUnsavedChangesStore()

async function onChange(engineId: unknown) {
  if (typeof engineId !== 'number' || engineId === value.value) return
  if (await unsaved.confirmLeave()) selection.pick(engineId)
}
</script>

<template>
  <div class="flex items-center gap-2" data-testid="engine-selector">
    <span :id="labelId" class="text-body-xs font-semibold whitespace-nowrap text-ink uppercase">
      Dynamic engine
    </span>

    <LoadingSpinner
      v-if="isPending"
      :label="LOADING_ENGINES"
      data-testid="engine-selector-loading"
    />
    <span
      v-else-if="isError"
      class="text-body-sm text-danger"
      role="status"
      data-testid="engine-selector-error"
    >
      Engines are not available right now.
    </span>

    <SelectRoot v-else :model-value="value" :disabled="!hasEngines" @update:model-value="onChange">
      <SelectTrigger
        :aria-labelledby="labelId"
        data-testid="engine-selector-trigger"
        class="flex h-10 w-45 shrink-0 items-center justify-between gap-2 rounded-md border-2 border-ink bg-surface pr-4 pl-2.5 text-body-sm text-ink data-[disabled]:cursor-not-allowed data-[disabled]:border-line"
      >
        <!-- Reka keeps the label each option had when it first rendered, so a rename in
             place would show the old name; render the selected engine's current name. -->
        <SelectValue class="truncate" data-testid="engine-selector-value">
          <template v-if="selectedEngine">{{ selectedEngine.name }}</template>
          <span v-else class="text-muted">{{ placeholder }}</span>
        </SelectValue>
        <!-- foreUP Design System "Chevron Down". -->
        <svg aria-hidden="true" viewBox="0 0 24 24" class="size-6 shrink-0 text-subtle">
          <path
            fill="currentColor"
            fill-rule="evenodd"
            d="M19.633 9.293a1 1 0 0 1 0 1.414l-6.963 6.963a1 1 0 0 1-1.414 0L4.293 10.707a1 1 0 0 1 1.414-1.414l6.256 6.256 6.256-6.256a1 1 0 0 1 1.414 0Z"
          />
        </svg>
      </SelectTrigger>
      <SelectPortal>
        <SelectContent
          position="popper"
          :side-offset="4"
          class="z-50 min-w-(--reka-select-trigger-width) rounded-md border border-line bg-surface py-1 shadow-md"
          data-testid="engine-selector-listbox"
        >
          <SelectViewport>
            <SelectItem
              v-for="engine in engines"
              :key="engine.id"
              :value="engine.id"
              :data-testid="`engine-selector-option-${engine.id}`"
              class="flex cursor-pointer items-center justify-between gap-4 px-2.5 py-1.5 text-body-sm text-ink outline-none data-[highlighted]:bg-canvas"
            >
              <SelectItemText>{{ engine.name }}</SelectItemText>
              <SelectItemIndicator aria-hidden="true">✓</SelectItemIndicator>
            </SelectItem>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  </div>
</template>
